import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { RulesContent } from "@/components/RulesContent";
import { useSettings } from "@/context/SettingsContext";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function RulesGate() {
  const { user, setUser } = useAuth();
  const { settings } = useSettings();
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only gate real logged-in members who haven't accepted yet.
  if (!user || user === null || user.rules_accepted_at) return null;

  const site = settings?.site_name || "Drops of Honey";

  const accept = async () => {
    if (!agree) return;
    setSaving(true);
    try {
      const { data } = await api.post("/rules/accept");
      setUser({ ...user, rules_accepted_at: data.rules_accepted_at });
      toast.success("Thanks for keeping the community kind!");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4" data-testid="rules-gate">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative surface border rounded-2xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col float-up">
        <div className="p-6 border-b" style={{ borderColor: "var(--page-border)" }}>
          <div className="flex items-center gap-2 mb-1"><HeartHandshake className="w-5 h-5 brand-text" /><p className="font-tech text-xs uppercase tracking-widest brand-text">Before you continue</p></div>
          <h2 className="font-display text-2xl font-bold">Welcome to {site} — our house rules</h2>
          <p className="text-muted-page text-sm mt-1">Please read and agree to our community rules to keep using the site.</p>
        </div>

        <div className="p-6 overflow-y-auto">
          <RulesContent compact />
        </div>

        <div className="p-6 border-t space-y-3" style={{ borderColor: "var(--page-border)" }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="rules-gate-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4 accent-amber-600" />
            <span className="text-sm">I have read and agree to follow the <Link to="/rules" target="_blank" className="brand-text font-semibold honey-underline">community rules</Link>. I understand I can be removed for belittling or harassing others.</span>
          </label>
          <button data-testid="rules-gate-accept" onClick={accept} disabled={!agree || saving} className="w-full py-3 rounded-full font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ background: "var(--brand)", color: "#fff" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Agree & continue
          </button>
        </div>
      </div>
    </div>
  );
}
